import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBanners } from '../../redux/menu/BannersSlice';
import { FaMapMarkerAlt, FaChevronDown } from 'react-icons/fa';
import useTheme from '../../hooks/useTheme';
import { getBannerBorderColor } from '../../utils/backgroundStyles';

const AutoSwipeBanner = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  const { banners, status } = useSelector((state) => state.banner);
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);

  // Extract banners from the new nested structure
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);

  // Get border color based on coverImage
  const coverImage = profile?.cover_image || profile?.theme?.coverImage || 'foody';
  const borderColor = getBannerBorderColor(coverImage);

  // Fetch banners when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchBanners(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Auto-swipe functionality
  useEffect(() => {
    if (!bannerList || bannerList.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === bannerList.length - 1 ? 0 : prevIndex + 1
      );
    }, 3500); // Change image every 3.5 seconds for more dynamic feel

    return () => clearInterval(interval);
  }, [bannerList]);

  // Close modal when clicking outside
  useEffect(() => {
    if (!showLocationModal) return;

    const handleClickOutside = () => {
      setShowLocationModal(false);
    };

    // Add a small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showLocationModal]);

  // Get unique locations from StoreGroup
  const getUniqueLocations = () => {
    if (!profile?.StoreGroup?.BranchList) return [];
    
    const uniqueLocations = [];
    const seenLocations = new Set();
    
    profile.StoreGroup.BranchList.forEach((branch, index) => {
      // Skip the first element
      if (index === 0) return;
      
      const locationLower = branch.location?.toLowerCase();
      if (locationLower && !seenLocations.has(locationLower)) {
        seenLocations.add(locationLower);
        uniqueLocations.push(branch);
      }
    });
    
    return uniqueLocations;
  };

  const handleLocationClick = () => {
    console.log('Location clicked');
    console.log('Profile:', profile);
    console.log('StoreGroup:', profile?.StoreGroup);
    console.log('BranchList:', profile?.StoreGroup?.BranchList);
    
    if (profile?.StoreGroup?.BranchList && profile.StoreGroup.BranchList.length > 0) {
      // Toggle modal - close if already open, open if closed
      if (showLocationModal) {
        console.log('Closing modal...');
        setShowLocationModal(false);
      } else {
        console.log('Opening modal...');
        
        // Get button position
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setButtonPosition({
            top: rect.bottom + 4, // 4px below button
            left: rect.left
          });
        }
        
        setShowLocationModal(true);
      }
    } else {
      console.log('No branches available');
    }
  };

  const handleBranchClick = (link) => {
    console.log('Navigating to:', link);
    // Navigate to the link in the same tab
    window.location.href = link;
    setShowLocationModal(false);
  };

  if (status === 'loading') {
    return (
      <div className="w-full h-36 sm:h-44 lg:h-96 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse rounded-lg sm:rounded-xl md:rounded-2xl mx-auto max-w-sm md:max-w-md lg:max-w-3xl xl:max-w-5xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4" style={{ borderColor: borderColor }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-600 text-xs sm:text-sm font-medium">Loading banners...</div>
        </div>
      </div>
    );
  }

  if ((status === 'succeeded' || status === 'failed') && (!bannerList || bannerList.length === 0)) {
    return null;
  }

  return (
    <div className="relative w-full max-w-sm md:max-w-md lg:max-w-3xl xl:max-w-5xl">
      {/* Location container - above banner */}
      <div className="flex justify-start mb-1 relative">
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLocationClick();
            }}
            type="button"
            className="relative rounded-md px-2 py-1 shadow-md border border-white/30 backdrop-blur-sm hover:scale-105 transition-transform duration-200 cursor-pointer z-10"
            style={{ background: theme.colors.locationBg, pointerEvents: 'auto' }}
          >
          <div className="flex items-center justify-center space-x-1" style={{ pointerEvents: 'none' }}>
            <FaMapMarkerAlt 
              className="drop-shadow-lg animate-pulse" 
              style={{ 
                fontSize: '10px',
                color: theme.colors.locationIcon 
              }} 
            />
            <span 
              className="font-bold poppins drop-shadow-lg tracking-wide" 
              style={{ 
                fontSize: '10px',
                color: theme.colors.locationText 
              }}
            >
              {profile?.name || 'Store'}
            </span>
            {profile?.StoreGroup?.BranchList && profile.StoreGroup.BranchList.length > 1 && (
              <FaChevronDown 
                className="drop-shadow-lg ml-0.5" 
                style={{ 
                  fontSize: '11px',
                  color: theme.colors.locationText,
                  opacity: 0.9
                }} 
              />
            )}
            <div 
              className="w-1.5 h-1.5 rounded-full animate-ping"
              style={{ backgroundColor: theme.colors.locationAccent }}
            ></div>
          </div>
          {/* Decorative elements - smaller */}
          <div 
            className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
          <div 
            className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
          <div 
            className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-bounce"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
        </button>

        {/* Location dropdown - positioned relative to button */}
        {showLocationModal && (
          <div 
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200"
            style={{ 
              zIndex: 40,
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 space-y-1 min-w-[120px]">
              {getUniqueLocations().map((branch, index) => (
                <button
                  key={index}
                  onClick={() => handleBranchClick(branch.link)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-primary/10 rounded transition-all duration-200"
                >
                  <FaMapMarkerAlt className="w-3 h-3 text-primary" />
                  <span className="font-semibold text-gray-800 poppins text-xs whitespace-nowrap">
                    {branch.location}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      
      <div className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4 backdrop-blur-sm bg-gradient-to-br from-white/10 to-white/5" style={{ borderColor: borderColor }}>
        {/* Banner Images */}
        <div className="relative h-36 sm:h-44 lg:h-96 w-full">
          {bannerList.map((banner, index) => (
            <div
              key={banner.id || index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
                index === currentIndex 
                  ? 'opacity-100 scale-100 translate-x-0' 
                  : 'opacity-0 scale-105 translate-x-4'
              }`}
            >
              <img
                src={banner.DocImage?.imageUrl}
                alt={`Banner ${index + 1}`}
                className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-110"
                style={{ objectFit: 'fill' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Gradient overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
            </div>
          ))}
        </div>
        
        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-primary/10 via-transparent to-primary/10 opacity-50"></div>
      </div>

      {/* Backdrop to close modal */}
      {showLocationModal && (
        <div 
          className="fixed inset-0 z-[9998]"
          onClick={() => setShowLocationModal(false)}
        />
      )}
    </div>
  );
};

export default AutoSwipeBanner;
